import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Dialog from 'material-ui/Dialog';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import trim from 'lodash/trim';
import {
  InputLabel,
  InputText,
  PasswordChangeBtn,
  ChangeButton, 
  CancelButton,
} from './styled';
import { authManager } from '../../helpers/managers';
import Loading from '../../components/Loading';

class UserPassword extends Component {

  constructor(props) {
    super(props);

    this.state = {
      modalOpen: false,
      updating: false,
      oldPassword: '',
      newPassword: '',
      newPassword2: '',
      error: '',
      newPasswordError: false,
    };
  }

  onInputStateChange = (statekey) => {
    return e => {
      const value = e.target.value;
      this.setState({
        error: '',
        newPasswordError: false,
        [statekey]: trim(value),
      });
    };
  };

  newPasswordMatch = () => {
    const { newPassword, newPassword2 } = this.state;
    if (isEmpty(newPassword) || isEmpty(newPassword2)) {
      return true;
    }

    return isEqual(newPassword, newPassword2);
  };

  newPasswordLengthValid = () => {
    const { newPassword } = this.state;
    const len = newPassword.length;
    return !(len > 0 && len < 6);
  };

  showModal = (modalOpen = false) => {
    this.setState({
      modalOpen,
    });
  };

  onModalClose = () => {
    this.showModal(false);
  };

  showChangeDialog = () => {
    this.showModal(true);
  };

  submitPassword = () => {
    this.setState({
      updating: true,
    }, async () => {
      try {
        const { email, onPasswordChange } = this.props;
        const { oldPassword, newPassword, newPassword2 } = this.state;

        if (isEmpty(newPassword) || isEmpty(newPassword2)) {
          throw new Error('New password cannot be empty.');
        }

        if (!this.newPasswordMatch()) {
          throw new Error('Please make sure you enter the same new password twice.');
        }

        if (!this.newPasswordLengthValid()) {
          throw new Error('Password should be at least six characters long.');
        }

        try {
          const user = authManager.getCurrentUser();
          if (isEmpty(user)) {
            throw new Error('Unable to change user password');
          }

          // re auth current user with credential
          await new Promise((resolve, reject) => {
            const credential = authManager.getEmailCredential(email, oldPassword);
            user
              .reauthenticateWithCredential(credential)
              .then(resolve)
              .catch(reject);
          });

          // update password
          await new Promise((resolve, reject) => {
            user
              .updatePassword(newPassword)
              .then(resolve)
              .catch(reject);
          });

          // for the last time try the new password
          await new Promise((resolve, reject) => {
            const credential = authManager.getEmailCredential(email, newPassword);
            user
              .reauthenticateWithCredential(credential)
              .then(resolve)
              .catch(reject);
          });

          this.setState({
            updating: false,
            modalOpen: false,
            oldPassword: '',
            newPassword: '',
            newPassword2: '',
          }, onPasswordChange);
        } catch (err) {
          const { code = '', message = '' } = err;
          if (!isEmpty(code) && !isEmpty(message)) {
            throw new Error(message);
          }

          console.log('Error while updating password: ', message, err);
          throw new Error('There is an error while updating password. Please try again later.');
        }
      } catch (e) {
        this.setState({
          updating: false,
          error: e.message,
        });
      }
    });
  };

  renderPasswordDialog() {
    const {
      modalOpen,
      updating,
      oldPassword,
      newPassword,
      newPassword2,
      error,
    } = this.state;

    const renderUpdateBtn = () => {
      const btnContent = updating ? (
        <Loading
          text="PROCESSING"
          type="circle"
          align="center"
          size={25}
          fontSize={1.4}
          color="#fff"
          fill="#fff"
          textStyle={{ margin: '0 5px' }}
        />
      ) : 'SUBMIT';

      return (
        <ChangeButton
          onClick={this.submitPassword}
          disabled={updating}
          className={`btn text-white text-medium ${updating ? 'disabled' : ''}`}
        >
        {btnContent}
        </ChangeButton>
      );
    };

    return (
      <Dialog
        title="Change Password"
        actions={[
          <CancelButton
            disabled={updating}
            onClick={this.onModalClose}
            className="btn text-medium margin-two-right naked"
          >
            CANCEL
          </CancelButton>,
          renderUpdateBtn()
        ]}
        modal={updating}
        open={modalOpen}
        onRequestClose={this.onModalClose}
        contentStyle={{ width: '95%', maxWidth: 560, transform: 'translate(0px, 15px)' }}
        bodyStyle={{ overflowY: 'auto' }}
        actionsContainerStyle={{
          padding: '20px 24px',
          border: '1px solid #f6f6f6',
        }}
        autoScrollBodyContent
      >
        <div className="row padding-five-top">
          <div className="col-lg-12 col-md-12 col-xs-12">
            <div className="alert alert-warning text-medium" role="alert">
              Hint: Password should be at least six characters long. To make it stronger, use upper and lower case letters, numbers, and symbols.
            </div>
          </div>
        </div>
        {!isEmpty(error) && (
          <div className="row">
            <div className="col-lg-12 col-md-12 col-xs-12">
              <div className="alert alert-danger text-medium" role="alert">{error}</div>
            </div>
          </div>
        )}
        <div className="row margin-five-bottom">
          <div className="col-lg-12 col-md-12 col-xs-12">
            <InputLabel>Current password</InputLabel>
            <InputText
              type="password"
              onChange={this.onInputStateChange('oldPassword')}
              value={oldPassword}
            />
          </div>
        </div>
        <div className="row margin-five-bottom">
          <div className="col-lg-12 col-md-12 col-xs-12">
            <InputLabel>New Password</InputLabel>
            <InputText
              type="password"
              onChange={this.onInputStateChange('newPassword')}
              value={newPassword}
              errored={!this.newPasswordMatch() || !this.newPasswordLengthValid()}
            />
          </div>
        </div>
        <div className="row margin-five-bottom">
          <div className="col-lg-12 col-md-12 col-xs-12">
            <InputLabel>Re-enter new password</InputLabel>
            <InputText
              type="password"
              onChange={this.onInputStateChange('newPassword2')}
              value={newPassword2}
              errored={!this.newPasswordMatch()}
            />
          </div>
        </div>
      </Dialog>
    );
  }

  render() {
    return (
      <React.Fragment>
        <PasswordChangeBtn
          onClick={this.showChangeDialog}>
          Change Password
        </PasswordChangeBtn>
        {this.renderPasswordDialog()}
      </React.Fragment>
    );
  }
}

UserPassword.propTypes = {
  email: PropTypes.string,
  onPasswordChange: PropTypes.func,
};

UserPassword.defaultProps = {
  email: '',
  onPasswordChange: () => {},
};

export default UserPassword;
